/**
 * Signal Registry — single source of truth for the media signal channels.
 *
 * Studio pages, the Communication Hub, and the sidebar badges all read channel
 * names, scored dimensions, and Marketplace paths from here. Keeping one
 * definition is what keeps Studio and Marketplace naming and fields in sync:
 * renaming a dimension or adding a channel happens once, in this file.
 *
 * Score semantics are shared across every channel: 'go' | 'cautious' | 'no-go'
 * | null (unscored), aggregated worst-first by computeOverall().
 */
import * as amService from './signalScoreService';
import * as fmService from './fmSignalScoreService';
import * as ubuntuService from './ubuntuSignalScoreService';

export const MARKETPLACE_BASE = 'https://www.kromarketplace.com';
export const MARKETPLACE_COMMUNICATION_HUB = `${MARKETPLACE_BASE}/communication-hub`;

/* ── Scored dimensions ──────────────────────────────────────────────────────
 * Each channel scores three channel-specific dimensions plus the two shared
 * dimensions (Canvas Art Storytelling, Mutual Gains) carried by all channels.
 */

// Domain extension signals
export const AM_DIMENSIONS = [
  { id: 'education', label: 'Education', desc: 'Individually and in your personal communities, how well do Learning & Development programs support your needs? And is there adequate funding flowing in for sustainability today, and thriving in the future?.' },
  { id: 'healthcare', label: 'Healthcare', desc: 'Individually and in your personal communities, how well do Learning & Development programs support your needs? And is there adequate funding flowing in for sustainability today, and thriving in the future?' },
  { id: 'accessibility', label: 'Accessibility', desc: 'Individually and in your personal communities, how well do Learning & Development programs support your needs? And is there adequate funding flowing in for sustainability today, and thriving in the future?' },
  { id: 'fineArtsIntegration', label: 'Canvas Art Storytelling', desc: 'How well are your leaders implementing canvas art galleries into the narrative of this impact journey, across education, healthcare, and accessibility, for you and your personal communities?' },
  { id: 'mutualValueFlow', label: 'Mutual Gain', desc: 'How well are your leaders implementing canvas art galleries into the narrative of this impact journey, across education, healthcare, and accessibility, for you and your personal communities?' }
];

// Domain / strategic extension signals
export const FM_DIMENSIONS = [
  { id: 'transparency', label: 'Transparency', desc: 'Do you feel included in the ESG data collection, analysis, and reporting process?' },
  { id: 'credibility', label: 'Credibility', desc: 'Do you feel included in the ESG data collection, analysis, and reporting process?' },
  { id: 'accountability', label: 'Accountability', desc: 'Do you feel included in the ESG data collection, analysis, and reporting process?' },
  { id: 'canvasArtStorytelling', label: 'Canvas Art Storytelling', desc: 'How well are your leaders weaving canvas art galleries into the narrative of this change journey, across core ESG data and reporting criteria?' },
  { id: 'mutualGains', label: 'Mutual Gains', desc: 'Balanced benefit exchange for stakeholders contributing or receiving FM-distributed content.' }
];

// Community signals. Follows the established AM/FM shape — three channel-specific
// dimensions plus the two shared ones (Canvas Art Storytelling, Mutual Gains).
export const UBUNTU_DIMENSIONS = [
  { id: 'communityVoice', label: 'Community Voice', desc: 'Individually and in your personal communities, how well are the people closest to the impact given a real say in the decisions that affect them?' },
  { id: 'sharedProsperity', label: 'Shared Prosperity', desc: 'Individually and in your personal communities, how fairly are gains distributed rather than concentrated away from the people who created them?' },
  { id: 'collectiveCare', label: 'Collective Care', desc: 'Individually and in your personal communities, how well does the network look after its most exposed participants when conditions get difficult?' },
  { id: 'canvasArtStorytelling', label: 'Canvas Art Storytelling', desc: 'How well are your leaders weaving canvas art galleries into the narrative of this change journey, across the communities you belong to?' },
  { id: 'mutualGains', label: 'Mutual Gains', desc: 'Balanced benefit exchange for stakeholders contributing to or receiving from community-distributed content.' }
];

/* ── Channels ─────────────────────────────────────────────────────────────── */

export const SIGNAL_CHANNELS = [
  {
    id: 'am',
    label: 'AM Signal',
    // Plural form used on the Marketplace media signal pages.
    marketplaceLabel: 'AM Signals',
    studioPath: '/signal/am-signal',
    marketplacePath: `${MARKETPLACE_BASE}/am-signals`,
    accent: '#6a1b9a',
    surface: '#faf7ff',
    border: '#e5dff0',
    heading: '#4a148c',
    exportType: 'AM',
    dimensions: AM_DIMENSIONS,
    service: amService,
    description: 'Domain extension signals — education, healthcare, and accessibility outcomes.',
  },
  {
    id: 'fm',
    label: 'FM Signal',
    marketplaceLabel: 'FM Signals',
    studioPath: '/signal/fm-signal',
    marketplacePath: `${MARKETPLACE_BASE}/fm-signals`,
    accent: '#1a237e',
    surface: '#f7faff',
    border: '#dfe5f0',
    heading: '#1a237e',
    exportType: 'FM',
    dimensions: FM_DIMENSIONS,
    service: fmService,
    description: 'ESG reporting signals — transparency, credibility, and accountability.',
  },
  {
    id: 'ubuntu',
    label: 'Ubuntu Signal',
    marketplaceLabel: 'Ubuntu Signals',
    studioPath: '/signal/ubuntu-signal',
    marketplacePath: `${MARKETPLACE_BASE}/ubuntu-signals`,
    accent: '#00695c',
    surface: '#f4fbf9',
    border: '#d8ece7',
    heading: '#00695c',
    exportType: 'UBUNTU',
    dimensions: UBUNTU_DIMENSIONS,
    service: ubuntuService,
    description: 'Community signals — collective voice, shared prosperity, and care.',
  },
];

export const getChannel = (id) => SIGNAL_CHANNELS.find(c => c.id === id);

/* ── Shared score helpers ─────────────────────────────────────────────────── */

export const SCORE_VALUES = ['go', 'cautious', 'no-go'];

export const SCORE_META = {
  'go':       { label: 'Very good',  tone: '#1b5e20', surface: '#e6f4ea' },
  'cautious': { label: 'Acceptable', tone: '#f57f17', surface: '#fef6e0' },
  'no-go':    { label: 'Needs attention', tone: '#b71c1c', surface: '#fdeaea' },
};

/**
 * Rolls a channel's scores into a single state, worst-first — matching the
 * existing per-channel computeOverall so the hub never disagrees with a page.
 */
export function computeOverall(scores) {
  const values = Object.values(scores || {}).filter(Boolean);
  if (values.includes('no-go')) return 'no-go';
  if (values.includes('cautious')) return 'cautious';
  if (values.length === 0) return null;
  return 'go';
}

/**
 * Population/visibility snapshot for one channel.
 *
 * Only the channel's currently registered dimensions are considered. Stored
 * scores can outlive a dimension rename, and counting those leftovers would let
 * the hub report a status the signal page itself never shows.
 */
export function summarizeChannel(channel, scores) {
  const total = channel.dimensions.length;
  const counts = { go: 0, cautious: 0, 'no-go': 0 };
  const known = {};
  let scored = 0;
  for (const dim of channel.dimensions) {
    const v = scores?.[dim.id];
    if (v && counts[v] !== undefined) { counts[v] += 1; scored += 1; known[dim.id] = v; }
  }
  return {
    channelId: channel.id,
    total,
    scored,
    unscored: total - scored,
    counts,
    overall: computeOverall(known),
    populated: scored > 0,
    completionPct: total ? Math.round((scored / total) * 100) : 0,
  };
}

/**
 * Subscribes to every channel at once and reports a keyed map of scores.
 * Returns a single unsubscribe function.
 *
 * @param {(scoresByChannel: Record<string, object>) => void} onChange
 */
export function subscribeAllSignals(onChange) {
  const scoresByChannel = {};
  const unsubs = SIGNAL_CHANNELS.map(channel =>
    channel.service.subscribe((scores) => {
      scoresByChannel[channel.id] = scores || {};
      onChange({ ...scoresByChannel });
    })
  );
  return () => unsubs.forEach(u => u && u());
}
